import { Pipe, PipeTransform } from '@angular/core';

import { getCountryName } from '../admin/form-controls/country-select/countries';
import { EventPairing, EventResult, EventResultFieldDef } from './models';

/**
 * Events store an ISO country code; users should never see the code itself.
 * A pipe rather than a component method so templates stay free of plain method
 * calls, per the workspace's Angular template performance rules.
 */
@Pipe({ name: 'countryName', standalone: true })
export class CountryNamePipe implements PipeTransform {
    transform(code: string | undefined | null): string {
        return getCountryName(code);
    }
}

/** The stored pairingMode values, mapped to the keys that name them for users. */
const PAIRING_MODE_KEYS: Record<string, string> = {
    swiss: 'GLOBAL.EVENTS.PAIRING_MODE.SWISS',
    round_robin: 'GLOBAL.EVENTS.PAIRING_MODE.ROUND_ROBIN',
    random: 'GLOBAL.EVENTS.PAIRING_MODE.RANDOM'
};

/**
 * Turns a stored pairing mode into a translation key, for piping through `translate`.
 * An unrecognised value (such as the retired `single_elimination`, which older event
 * documents may still hold) passes through unchanged rather than rendering as a
 * missing translation key.
 */
@Pipe({ name: 'pairingModeKey', standalone: true })
export class PairingModeKeyPipe implements PipeTransform {
    transform(mode: string | undefined | null): string {
        if (!mode) {
            return '';
        }
        return PAIRING_MODE_KEYS[mode] ?? mode;
    }
}

const PUBLISH_STATUS_KEYS: Record<string, string> = {
    draft: 'GLOBAL.EVENTS.PUBLISH_STATUS.DRAFT',
    published: 'GLOBAL.EVENTS.PUBLISH_STATUS.PUBLISHED',
    cancelled: 'GLOBAL.EVENTS.PUBLISH_STATUS.CANCELLED'
};

/**
 * Publish status is an organiser's view of their own event — draft, live, or
 * cancelled — not something a visitor should see on every card; the templates
 * gate on ownership.
 */
@Pipe({ name: 'publishStatusKey', standalone: true })
export class PublishStatusKeyPipe implements PipeTransform {
    transform(status: string | undefined | null): string {
        if (!status) {
            return '';
        }
        return PUBLISH_STATUS_KEYS[status] ?? status;
    }
}

const VISIBILITY_KEYS: Record<string, string> = {
    public: 'GLOBAL.EVENTS.VISIBILITY.PUBLIC',
    private: 'GLOBAL.EVENTS.VISIBILITY.PRIVATE'
};

/**
 * Names an event's visibility setting for the organiser who chose it.
 *
 * A missing value is read as 'public' — the schema's own default
 * (api/src/event/event.schema.ts) — rather than rendered blank. An event saved
 * before that default existed, or through a path that never touched the
 * collapsed Registration section the visibility field lives in, can otherwise
 * reach here with no value at all, and a blank line under the Visibility header
 * reads as a broken page rather than as "unset, defaulting to public".
 */
@Pipe({ name: 'visibilityKey', standalone: true })
export class VisibilityKeyPipe implements PipeTransform {
    transform(visibility: string | undefined | null): string {
        const key = visibility || 'public';
        return VISIBILITY_KEYS[key] ?? key;
    }
}

/** Ionic colour for a publish status: live, cancelled, or neither yet. */
@Pipe({ name: 'publishStatusColour', standalone: true })
export class PublishStatusColourPipe implements PipeTransform {
    transform(status: string | undefined | null): string {
        switch (status) {
            case 'published':
                return 'success';
            case 'cancelled':
                return 'danger';
            default:
                return 'medium';
        }
    }
}

/**
 * A list slot's own display name, or a sensible stand-in — never a bare em-dash
 * with nothing in front of it.
 *
 * The lone slot in a single-list event doesn't need a name — there is nothing
 * else it could be — so an unnamed slot there renders nothing, and the details
 * page shows only its points value. Once an event has more than one slot, every
 * entry needs something to read, so an unnamed slot among others falls back to
 * "Default" rather than leaving a blank space before its own em-dash and points.
 */
@Pipe({ name: 'listSlotLabel', standalone: true })
export class ListSlotLabelPipe implements PipeTransform {
    transform(slot: { label?: string } | undefined | null, totalSlots: number): string {
        const label = slot?.label?.trim();
        if (label) {
            return label;
        }
        return totalSlots > 1 ? 'Default' : '';
    }
}

/**
 * A compact "1000pts" (one slot) or "1000/1500pts" (an escalation-format event
 * with several) summary for a list of an event's list slots — an event has no
 * single top-level points limit of its own, only per-slot ones.
 */
@Pipe({ name: 'listSlotsPointsSummary', standalone: true })
export class ListSlotsPointsSummaryPipe implements PipeTransform {
    transform(slots: { pointsLimit?: number }[] | undefined | null): string {
        const points = (slots ?? []).map((s) => s.pointsLimit).filter((p): p is number => !!p);
        if (!points.length) {
            return '';
        }
        return `${points.join('/')}pts`;
    }
}

/**
 * A reported result as one readable line: who won, then each field's two values.
 *
 * A pipe rather than a component method because the organiser's round page renders one
 * per pairing inside nested loops, and a pure pipe only recomputes when its inputs
 * actually change -- the workspace rule against calling plain methods from templates.
 */
@Pipe({ name: 'resultSummary', standalone: true })
export class ResultSummaryPipe implements PipeTransform {
    transform(result: EventResult | undefined, pairing: EventPairing | undefined, resultFields: EventResultFieldDef[] = []): string {
        if (!result || !pairing) {
            return '';
        }

        const nameA = pairing.participantA?.displayName || pairing.participantA?.userId || 'A';
        const nameB = pairing.participantB?.displayName || pairing.participantB?.userId || 'B';
        const winner = result.outcome === 'DRAW' ? 'Draw' : `${result.outcome === 'A_WIN' ? nameA : nameB} won`;

        const scores = resultFields
            .map((field) => {
                const key = String(field.id);
                const a = result.participantAValues?.[key];
                const b = result.participantBValues?.[key];
                if (a == null && b == null) {
                    return null;
                }
                return `${field.label} ${a ?? '-'}–${b ?? '-'}`;
            })
            .filter((entry): entry is string => entry !== null);

        return scores.length ? `${winner} · ${scores.join(' · ')}` : winner;
    }
}
